/**
 * IndexedDB service for client-side data caching
 * Provides instant UI with zero network requests for user data
 */

import { Order } from '../types';

const DB_NAME = 'FoxieSystemsDB';
const DB_VERSION = 1;

/**
 * User data structure (matches backend bundle)
 */
export interface UserProfile {
  uid: string;
  email: string;
  displayName: string;
  roles: string[];
  permissions: string[];
  createdAt: string;
}

export interface OwnedProduct {
  id: string;
  title: string;
  category: string;
  purchasedAt: string;
  orderId: string;
  downloadCount: number;
  downloadLimit: number;
}

export interface UserStatistics {
  totalSpent: number;
  totalOrders: number;
  totalProducts: number;
}

export interface UserDataCache {
  profile?: UserProfile;
  orders: Order[];
  ownedProducts: OwnedProduct[];
  statistics?: UserStatistics;
  checksum?: string;
  lastSync?: number;
}

/**
 * Initialize IndexedDB database
 */
export async function initDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;

      // Store for public data (categories, products)
      if (!db.objectStoreNames.contains('publicData')) {
        const publicStore = db.createObjectStore('publicData', { keyPath: 'key' });
        publicStore.createIndex('timestamp', 'timestamp', { unique: false });
      }

      // Store for user data (orders, profile, owned products)
      if (!db.objectStoreNames.contains('userData')) {
        const userStore = db.createObjectStore('userData', { keyPath: 'key' });
        userStore.createIndex('lastSync', 'lastSync', { unique: false });
      }

      // Store for individual orders (for fast queries)
      if (!db.objectStoreNames.contains('orders')) {
        const ordersStore = db.createObjectStore('orders', { keyPath: 'id' });
        ordersStore.createIndex('createdAt', 'createdAt', { unique: false });
        ordersStore.createIndex('status', 'status', { unique: false });
        ordersStore.createIndex('userId', 'userId', { unique: false });
      }

      // Store for owned products (for fast queries)
      if (!db.objectStoreNames.contains('ownedProducts')) {
        const productsStore = db.createObjectStore('ownedProducts', { keyPath: 'id' });
        productsStore.createIndex('category', 'category', { unique: false });
        productsStore.createIndex('purchasedAt', 'purchasedAt', { unique: false });
      }

      console.log('IndexedDB initialized with schema version', DB_VERSION);
    };
  });
}

/**
 * Get data from IndexedDB store
 */
export async function getFromDB<T>(storeName: string, key: string): Promise<T | null> {
  const db = await initDB();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, 'readonly');
    const store = transaction.objectStore(storeName);
    const request = store.get(key);

    request.onsuccess = () => resolve(request.result?.value || null);
    request.onerror = () => reject(request.error);
  });
}

/**
 * Put data into IndexedDB store
 */
export async function putInDB(storeName: string, key: string, value: any): Promise<void> {
  const db = await initDB();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, 'readwrite');
    const store = transaction.objectStore(storeName);
    const request = store.put({ key, value, timestamp: Date.now() });

    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}

/**
 * Get user data cache
 */
export async function getUserDataCache(): Promise<UserDataCache | null> {
  return getFromDB<UserDataCache>('userData', 'cache');
}

/**
 * Store complete user data cache
 */
export async function storeUserDataCache(data: UserDataCache): Promise<void> {
  const db = await initDB();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction(['userData', 'orders', 'ownedProducts'], 'readwrite');

    // Store complete cache
    const userStore = transaction.objectStore('userData');
    userStore.put({
      key: 'cache',
      value: {
        ...data,
        lastSync: Date.now(),
      },
      lastSync: Date.now(),
    });

    // Store individual orders for fast queries
    const ordersStore = transaction.objectStore('orders');
    ordersStore.clear(); // Clear old data
    data.orders.forEach(order => {
      ordersStore.put(order);
    });

    // Store owned products for fast queries
    const productsStore = transaction.objectStore('ownedProducts');
    productsStore.clear(); // Clear old data
    data.ownedProducts.forEach(product => {
      productsStore.put(product);
    });

    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error);
  });
}

/**
 * Get all orders from IndexedDB
 */
export async function getOrdersFromDB(): Promise<Order[]> {
  const db = await initDB();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction('orders', 'readonly');
    const store = transaction.objectStore('orders');
    const request = store.getAll();

    request.onsuccess = () => resolve(request.result || []);
    request.onerror = () => reject(request.error);
  });
}

/**
 * Get orders by status
 */
export async function getOrdersByStatus(status: string): Promise<Order[]> {
  const db = await initDB();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction('orders', 'readonly');
    const store = transaction.objectStore('orders');
    const index = store.index('status');
    const request = index.getAll(status);

    request.onsuccess = () => resolve(request.result || []);
    request.onerror = () => reject(request.error);
  });
}

/**
 * Get owned products from IndexedDB
 */
export async function getOwnedProductsFromDB(): Promise<OwnedProduct[]> {
  const db = await initDB();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction('ownedProducts', 'readonly');
    const store = transaction.objectStore('ownedProducts');
    const request = store.getAll();

    request.onsuccess = () => resolve(request.result || []);
    request.onerror = () => reject(request.error);
  });
}

/**
 * Get owned products by category
 */
export async function getOwnedProductsByCategory(category: string): Promise<OwnedProduct[]> {
  const db = await initDB();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction('ownedProducts', 'readonly');
    const store = transaction.objectStore('ownedProducts');
    const index = store.index('category');
    const request = index.getAll(category);

    request.onsuccess = () => resolve(request.result || []);
    request.onerror = () => reject(request.error);
  });
}

/**
 * Clear all user data from IndexedDB (for logout)
 */
export async function clearUserData(): Promise<void> {
  const db = await initDB();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction(['userData', 'orders', 'ownedProducts'], 'readwrite');

    transaction.objectStore('userData').clear();
    transaction.objectStore('orders').clear();
    transaction.objectStore('ownedProducts').clear();

    transaction.oncomplete = () => {
      console.log('✅ User data cleared from IndexedDB');
      resolve();
    };
    transaction.onerror = () => reject(transaction.error);
  });
}

/**
 * Clear all data from IndexedDB
 */
export async function clearAllData(): Promise<void> {
  const db = await initDB();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction(
      ['publicData', 'userData', 'orders', 'ownedProducts'],
      'readwrite'
    );

    transaction.objectStore('publicData').clear();
    transaction.objectStore('userData').clear();
    transaction.objectStore('orders').clear();
    transaction.objectStore('ownedProducts').clear();

    transaction.oncomplete = () => {
      console.log('✅ All data cleared from IndexedDB');
      resolve();
    };
    transaction.onerror = () => reject(transaction.error);
  });
}
